import { Assert, assertExists } from '@cotera/utilities';
import { AST } from '../../ast';
import { EraQlAst } from '../eraql-ast/ast';
import { _FunctionCall, _MakeStruct, _Scalar } from '../../ast/expr';
import _ from 'lodash';
import { Operator } from '../lex/tokens';
import { withNullMeta } from '../lex/types';
import { fromAssoc, fromList, fromScalar, fromSymbol } from './helpers';
import { tyToEraQlAst } from './ty-to-eraql-ast';

type Opts = { refs: Record<string, string> };

export function exprToEraQLAst(
  expr: AST.ExprFR,
  opts: Opts = { refs: {} }
): EraQlAst {
  switch (expr.t) {
    case 'scalar':
      return fromScalar(expr.val, expr.ty);

    case 'attr': {
      // TODO: add a test case for when base is non-empty.
      const base = opts.refs?.[expr.source] ?? '';
      return {
        t: 'attr-access',
        in: fromSymbol(base),
        attr: withNullMeta({ t: 'ident', name: expr.name }),
      };
    }

    case 'make-struct':
      return fromAssoc(expr.fields, (e) => exprToEraQLAst(e, opts));

    case 'make-array':
      return fromList(expr.elements, (e) => exprToEraQLAst(e, opts));

    case 'function-call':
      return fromFunctionCall(expr, opts);

    case 'case':
      return fromCases(expr, opts);

    case 'categorize':
      return {
        t: 'call',
        op: fromSymbol('categorize'),
        args: [
          {
            t: 'list',
            elems: expr.logic.map((logicElem) =>
              withNullMeta(fromCategorizeElem(logicElem, opts))
            ),
          } as const,
        ].map(withNullMeta),
        display: 'fc',
      };

    case 'get-field':
      return {
        t: 'call',
        op: fromSymbol('get'),
        args: [
          exprToEraQLAst(expr.expr, opts),
          { t: 'literal', val: expr.name } as const,
        ].map(withNullMeta),
        display: 'fc',
      };

    case 'cast':
      return {
        t: 'call',
        op: fromSymbol('cast'),
        args: [
          exprToEraQLAst(expr.expr, opts),
          tyToEraQlAst(expr.targetTy),
        ].map(withNullMeta),
        display: 'fc',
      };

    /**
     * Replicates the behavior of legacy toEraQLAst(),
     * However, a bug in the compiler does not handle this.
     *
     * TODO: fix
     */
    case 'invariants':
      return {
        t: 'call',
        op: fromSymbol('invariants'),
        args: [
          exprToEraQLAst(expr.expr, opts),
          fromAssoc(expr.invariants, (e) => exprToEraQLAst(e, opts)),
        ].map(withNullMeta),
        display: 'fc',
      };

    case 'window':
      return fromWindow(expr, opts);

    case 'expr-var': {
      const { scope, name, ty, default: defaultExpr } = expr;
      const params = fromAssoc(
        {
          scope: { t: 'literal', val: scope },
          name: { t: 'literal', val: name },
          ty: tyToEraQlAst(ty),
          ...(defaultExpr
            ? { default: exprToEraQLAst(defaultExpr, opts) }
            : {}),
        } satisfies Record<string, EraQlAst>,
        (e) => e
      );

      return {
        t: 'call',
        op: fromSymbol('ExprVar'),
        args: [withNullMeta(params)],
        display: 'fc',
      };
    }

    // The following two items were also un implemented
    // in the legacy code generator

    case 'macro-expr-case':
      throw new Error('TODO: unimplemented experToEraQLAst(macro-expr-case)');

    case 'macro-apply-vars-to-expr':
      throw new Error(
        'TODO: unimplemented experToEraQLAst(macro-apply-vars-to-expr)'
      );

    default:
      return Assert.unreachable(expr);
  }
}

// todo: create an overload of this function to handle the case where T
// is already EraQLAst
function fromFunctionCall(
  functionCall: Extract<AST.ExprFR, { t: 'function-call' }>,
  opts: Opts
): EraQlAst {
  const { op, args: argsExprs } = functionCall;

  const args = argsExprs.map((e) => exprToEraQLAst(e, opts)).map(withNullMeta);

  if (op === 'empty_array_of') {
    const [ty, ..._rest] = argsExprs;
    if (ty && ty.t === 'scalar' && ty.val === 'int') {
      return fromList([], (never) => never);
    }
  }

  if (op === 'null_of') {
    Assert.assert(args.length == 1);
    const [null_of_arg, _] = assertExists(args[0]);
    Assert.assert(null_of_arg.t === 'literal');
    Assert.assert(typeof null_of_arg.val === 'string');
    return {
      t: 'call',
      op: fromSymbol(op),
      args: [withNullMeta({ t: 'symbol', sym: null_of_arg.val })],
      display: 'fc',
    };
  }

  const infixChar = infixOpToChar(op);

  if (
    infixChar &&
    args.every(
      ([argAst, _argMeta]) => argAst.t !== 'call' || argAst.display !== 'pipe'
    )
  ) {
    return {
      t: 'call',
      op: withNullMeta({ t: 'operator', op: infixChar }),
      args,
      display: 'infix',
    };
  }

  let display: AST.FunctionCallDisplay = 'pipe';

  const [firstArg, ...restArgs] = args;

  if (
    // Format 0 arity or calls where everything is a lit/sym ident as a function call
    firstArg === undefined ||
    args.every(
      ([{ t }, _meta]) => t === 'symbol' || t === 'ident' || t === 'literal'
    )
  ) {
    display = 'fc';
  } else if (restArgs.length === 0) {
    const [firstArgAst, _meta] = firstArg;
    if (firstArgAst.t !== 'call') {
      display = 'fc';
    }
  }

  switch (op) {
    case 'format':
    case 'format_nullable':
      display = 'template';
      break;
  }

  return {
    t: 'call',
    op: fromSymbol(op),
    args,
    display,
  };
}

function fromCases(
  caseExpr: Extract<AST.ExprFR, { t: 'case' }>,
  opts: Opts
): EraQlAst {
  const { cases, else: elseCase } = caseExpr;

  const casesEraQLAst: EraQlAst[] = cases.map((caseFields) =>
    fromAssoc(caseFields, (e) => exprToEraQLAst(e, opts))
  );

  const argsEraQLAst: EraQlAst[] = [
    fromList(casesEraQLAst, (x) => x),
    ...(elseCase
      ? [fromAssoc({ else: elseCase }, (e) => exprToEraQLAst(e, opts))]
      : []),
  ];

  return {
    t: 'call',
    op: fromSymbol('case'),
    args: argsEraQLAst.map(withNullMeta),
    display: 'fc',
  };
}

function fromWindow(
  window: Extract<AST.ExprFR, { t: 'window' }>,
  opts: Opts
): EraQlAst {
  const {
    op,
    args,
    frame,
    over: { orderBy, partitionBy },
  } = window;

  const frameOption: EraQlAst | null =
    frame &&
    fromAssoc(
      {
        ...(frame.preceding !== null ? { preceding: frame.preceding } : {}),
        ...(frame.following !== null ? { following: frame.following } : {}),
      },
      (val: number | 'unbounded') => ({ t: 'literal', val })
    );

  const orderByOption: EraQlAst | null =
    orderBy.length > 0
      ? fromList(fromWindowOrderings(orderBy, opts), (x) => x)
      : null;

  const partitionByOption: EraQlAst | null =
    partitionBy.length > 0
      ? fromList(partitionBy, (e) => exprToEraQLAst(e, opts))
      : null;

  const allArgs: EraQlAst[] = [
    ...args.map((e) => exprToEraQLAst(e, opts)),
    fromAssoc(
      {
        ...(orderByOption ? { order_by: orderByOption } : {}),
        ...(partitionByOption ? { partition_by: partitionByOption } : {}),
        ...(frameOption ? { frame: frameOption } : {}),
      },
      (x) => x
    ),
  ];

  return {
    t: 'call',
    op: fromSymbol(`${op}_over`),
    args: allArgs.map(withNullMeta),
    display: 'fc',
  };
}

const infixOpToChar = (op: AST.FunctionIdentifier): Operator | null => {
  switch (op) {
    case 'add':
      return `+`;
    case 'sub':
      return `-`;
    case 'mul':
      return `*`;
    case 'div':
      return `/`;
    case 'to_the_power_of':
      return `^`;
    case 'eq':
      return `==`;
    case 'neq':
      return `!=`;
    case 'gte':
      return `>=`;
    case 'gt':
      return `>`;
    case 'lte':
      return `<=`;
    case 'lt':
      return `<`;
    case 'coalesce':
      return `??`;
    case 'and':
      return `&&`;
    case 'or':
      return `||`;
    default:
      return null;
  }
};

function fromCategorizeElem(
  logicElem: Extract<AST.ExprFR, { t: 'categorize' }>['logic'][number],
  opts: Opts
): EraQlAst {
  const { category, expr, filter } = logicElem;

  const filterAST =
    filter && fromAssoc({ filter }, (e) => exprToEraQLAst(e, opts));

  return {
    t: 'list',
    elems: [
      withNullMeta({ t: 'literal', val: category }),
      withNullMeta(exprToEraQLAst(expr, opts)),
      ...(filterAST ? [withNullMeta(filterAST)] : []),
    ],
  };
}

function fromWindowOrderings(
  orderings: Extract<AST.ExprFR, { t: 'window' }>['over']['orderBy'],
  opts: Opts
): EraQlAst[] {
  return orderings.map(({ expr, direction }) => {
    const exprEraQLAst = exprToEraQLAst(expr, opts);
    return direction === 'asc'
      ? exprEraQLAst
      : {
          t: 'call',
          op: fromSymbol('desc'),
          args: [withNullMeta(exprEraQLAst)],
          display: 'fc',
        };
  });
}
